'use strict';

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import {getAggregateState} from '@uw-it-sis/lib-react/lib/StateUtils';

import courseSummaryStore from './CourseSummaryStore';
import CourseMessagesStore from './CourseMessagesStore';

import events from '@uw-it-sis/lib-react/lib/Events';
import BookmarkEvents from './BookmarkEvents';

import { flow, action, observe, observable, configure, makeObservable } from "mobx";
configure({enforceActions: 'always'});

/**
 * The store for the CourseSummary(Popover)Widget. Basically just aggregates the CourseSummaryStore and
 * CourseMessagesStore.
 *
 * This store has to be instantiated.
 *
 */
class CourseSummaryWidgetStore {

    state = States.initial;
    toggleModal = false;

    constructor(courseId, courseCode) {
        makeObservable(this, {
            state: observable,
            toggleModal: observable,
            onBookmarkAdded: action,
            onBookmarkDeleted: action,
            checkDataLoadStates: action,
            resetState: action,
            updateToggle: action
        });

        this.baseUrl = 'api/courses';

        // This is the object to track the status of the loads.
        this.dataLoadStates = {
            courseSummary: States.initial,
            courseMessages: States.initial
        };
    }

    /**
     * Initialize.
     */
    mount(courseId, courseCode) {
        this.courseId = courseId;
        this.courseCode = courseCode;

        /*
         * Setup state observers for stores which manage data on which this store depends.
         */
        this.courseSummaryStoreDisposer = observe(courseSummaryStore, "state", (change) => {
            // Set the data state for this store to the new value.
            this.dataLoadStates.courseSummary = change.newValue;
            // Precipitate a UI update if the data loads have completed.
            this.checkDataLoadStates();
        });

        // Create a dedicated CourseMessagesStore instance.
        this.courseMessagesStore = new CourseMessagesStore(courseCode);
        this.courseMessagesStoreDisposer = observe(this.courseMessagesStore, "state", (change) => {
            // Set the data state for this store to the new value.
            this.dataLoadStates.courseMessages = change.newValue;
             // Precipitate a UI update if the data loads have completed.
            this.checkDataLoadStates();
        });

        /*
         * Listen for bookmark adds and removes and plan item adds so that course messages can be updated if appropriate.
         */
        events.listen(BookmarkEvents.added, (bookmark) => this.onBookmarkAdded(bookmark));
        events.listen(BookmarkEvents.deleted, (code) => this.onBookmarkDeleted(code));

    }

    /**
     * Dispose all observers.
     */
    unmount() {
        this.courseSummaryStoreDisposer();
        this.courseMessagesStoreDisposer();
        events.remove(BookmarkEvents.added, (bookmark) => this.onBookmarkAdded(bookmark));
        events.remove(BookmarkEvents.deleted, (code) => this.onBookmarkDeleted(code));
        this.updateToggle(false);
    }

    /**
     * Listener for bookmark adds.
     * @param bookmark The new bookmark.
     */
    onBookmarkAdded(bookmark) {
        if (appStore.isDebug()) {
            console.log("CourseSummaryWidgetStore.onBookmarkAdded: Got [%s]", JSON.stringify(bookmark));
        }
        if (bookmark.code === this.courseCode) {
             if (appStore.isDebug()) {
                 console.log("CourseSummaryWidgetStore.onBookmarkAdded: Reloading course messages because [%s] was added.", bookmark.code);
             }
             this._loadCourseMessages();
        }
    }

    /**
     * Listener for bookmark deletes.
     * @param code The code (course code or program code) of the bookmark which was removed.
     */
    onBookmarkDeleted(code) {
        if (appStore.isDebug()) {
            console.log(`CourseSummaryWidgetStore.onBookmarkDeleted: Deleting bookmark [${code}]`);
        }
        if (code === this.courseCode) {
             if (appStore.isDebug()) {
                 console.log(`CourseSummaryWidgetStore.onBookmarkAdded: Reloading course messages because [${code}] was removed.`);
             }
             this._loadCourseMessages();
        }
    }

    /**
     * Initiate course messages (re)load.
     * @private
     */
    _loadCourseMessages() {
        // Only attempt to load data for authenticated users. Otherwise, just set the data load state to done.
        if (appStore.isAuthenticated()) {
            this.courseMessagesStore.loadCourseMessages();
        } else {
            if (appStore.isDebug()) {
               console.log("Skipped the course messages data load because the user wasn't authenticated.");
            }
            this.dataLoadStates.courseMessages = States.done;
            this.checkDataLoadStates();
        }
    }

    /**
     * Update state if necessary.
     */
    checkDataLoadStates() {
        this.state = getAggregateState(this.dataLoadStates);
    }

    /**
     * Initialize the data.
     */
    initialize = flow(function* () {
        this.state = States.pending;

        // Don't yield for these. The observers defined in the constructor will resolve them.
        courseSummaryStore.loadCourseSummary(this.courseCode, this.courseId);
        this._loadCourseMessages();
    }.bind(this));

    /**
     * Pass-thru to get course summary data.
     * @param courseCode
     */
    getCourseSummaryData(courseCode){
        return courseSummaryStore.getCourseSummary(courseCode);
    }

    /**
     * Pass-thru to get course messages data.
     */
    getCourseMessagesData() {
        return this.courseMessagesStore.getCourseMessages();
    }

    resetState() {
        this.state = States.initial;
    }

    /**
     * Updates the value for toggleModal
     * @param toggle boolean
     */
    updateToggle(toggle) {
        this.toggleModal = toggle;
    }
}

export default CourseSummaryWidgetStore;